import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback
} from 'react';

// Optional: If you want a separate file for the debounce hook, you can.
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({
    items: [],
    loading: true,
    error: null,
    checksum: null,
    payment: {
      intentId: null,
      clientSecret: null,
      status: 'idle', // 'idle' | 'loading' | 'error' | 'success'
      error: null
    }
  });

  // Debounce payment intent updates
  const debouncedItems = useDebounce(cart.items, 500);

  const calculateChecksum = (items) => {
    if (!items.length) return null;
    const sortedItems = [...items].sort((a, b) => a.id - b.id);
    const itemString = sortedItems
      .map(item => `${item.id}-${item.type}-${item.quantity}-${item.price_cents}`)
      .join('|');

    return hashString(itemString);
  };

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash &= hash; // Convert to 32bit integer
    }
    return hash.toString(36);
  };

  const fetchCart = useCallback(async () => {
    try {
      setCart(prev => ({ ...prev, loading: true, error: null }));
      const response = await fetch('/carts/show.json');
      if (!response.ok) throw new Error('Failed to fetch cart');
      const data = await response.json();

      setCart(prev => ({
        ...prev,
        items: data,
        loading: false,
        error: null,
        checksum: calculateChecksum(data)
      }));
    } catch (error) {
      setCart(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  }, []);

  const setupCheckout = useCallback(async () => {
    try {
      setCart(prev => ({
        ...prev,
        payment: { ...prev.payment, status: 'loading', error: null }
      }));

      const response = await fetch('/setup_checkout');
      if (!response.ok) throw new Error('Failed to setup checkout');

      const data = await response.json();
      setCart(prev => ({
        ...prev,
        payment: {
          ...prev.payment,
          intentId: data.paymentIntentId,
          clientSecret: data.clientSecret,
          status: 'success',
          error: null
        }
      }));
    } catch (error) {
      setCart(prev => ({
        ...prev,
        payment: {
          ...prev.payment,
          status: 'error',
          error: error.message
        }
      }));
    }
  }, []);

  useEffect(() => {
    // On mount, fetch cart and set up checkout
    (async () => {
      await fetchCart();
      await setupCheckout();
    })();
  }, [fetchCart, setupCheckout]);

  // Debounced: If items changed, update PaymentIntent
  useEffect(() => {
    if (cart.items.length && cart.payment.intentId) {
      updatePaymentIntent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedItems]);

  const updatePaymentIntent = async () => {
    if (!cart.items.length || !cart.payment.intentId) return;

    try {
      setCart(prev => ({
        ...prev,
        payment: { ...prev.payment, status: 'loading', error: null }
      }));

      const response = await fetch('/update_checkout', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("meta[name='csrf-token']").getAttribute('content')
        },
        body: JSON.stringify({
          payment_intent_id: cart.payment.intentId,
          items: cart.items
        })
      });
      if (!response.ok) throw new Error('Failed to update payment intent');

      const data = await response.json();
      setCart(prev => ({
        ...prev,
        payment: {
          ...prev.payment,
          clientSecret: data.clientSecret,
          status: 'success',
          error: null
        }
      }));
    } catch (error) {
      setCart(prev => ({
        ...prev,
        payment: {
          ...prev.payment,
          status: 'error',
          error: error.message
        }
      }));
    }
  };

  const addItem = async (itemData) => {
    try {
      const response = await fetch('/cart_items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("meta[name='csrf-token']").getAttribute('content')
        },
        body: JSON.stringify([{  // Wrap in array to match backend's _json expectation
          item_type: itemData.item_type,
          item_id: itemData.item_id,
          quantity: itemData.quantity || 1,
          price_cents: itemData.price_cents,
          currency: itemData.currency || 'USD',
          tax_code: itemData.tax_code,
          item: itemData.item
        }])
      });
      
      if (!response.ok) throw new Error('Failed to add item');
      
      const data = await response.json();
      
      setCart(prev => {
        const updatedItems = [...prev.items, itemData];
        return {
          ...prev,
          items: updatedItems,
          checksum: calculateChecksum(updatedItems)
        };
      });

      return data;
    } catch (error) {
      console.error('Error in addItem:', error);
      throw error;
    }
  };

  const removeItem = async (itemId) => {
    try {
      const response = await fetch(`/cart_items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("meta[name='csrf-token']").getAttribute('content')
        }
      });
      if (!response.ok) throw new Error('Failed to remove item');

      setCart(prev => {
        const updatedItems = prev.items.filter(item => item.id !== itemId);
        return {
          ...prev,
          items: updatedItems,
          checksum: calculateChecksum(updatedItems)
        };
      });
    } catch (error) {
      throw new Error(`Failed to remove item: ${error.message}`);
    }
  };

  // Expose these as the public API
  const value = {
    items: cart.items,
    loading: cart.loading,
    error: cart.error,
    checksum: cart.checksum,

    payment: cart.payment,

    // Methods
    addItem,
    removeItem,
    refreshCart: fetchCart,
    updatePaymentIntent
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
